/**
 * Sound, synthesized in the browser: the effects (sfx/, routed here), and the music — composed tracks led by
 * the guzheng and guqin (music/) — played through the music bus. Real recordings can replace the
 * voices later without changing who calls what.
 */
import { music } from './music';
import { MusicEngine } from './music/engine';
import { sfxEngine } from './sfx';
import { legacySfx } from './sfx/legacy';

/**
 * A track id from music/tracks (or an old mood name: town, field, snow, battle, boss, jail), or
 * 'none' for silence.
 */
export type Theme = string;

export class Sfx {
  private ctx: AudioContext | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private engine: MusicEngine | null = null;
  muted = false;
  /** Effects volume and music volume, 0–1. */
  volume = 1;
  music = 0.5;

  constructor() {
    // Screens without the Sfx at hand (the login) unlock the sound through the music API.
    music.setUnlock(() => this.unlock());
    sfxEngine.unlocker = () => this.unlock();
  }

  /** The audio context once a gesture made it (the front's ambience, front/audio.ts). */
  get context(): AudioContext | null {
    return this.ctx;
  }
  /** The effects bus the ambience plays through (it follows the effects volume and mute). */
  get sfxOut(): GainNode | null {
    return this.sfxBus;
  }

  /** Must be called from a user gesture. */
  unlock(): void {
    // Made before any gesture (straight into the world after a reload): browsers hold it
    // suspended until one comes.
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      const a = new AudioContext();
      this.ctx = a;
      this.sfxBus = a.createGain();
      this.musicBus = a.createGain();
      // A little room around the effects (the music has its own hall).
      const room = a.createConvolver();
      room.buffer = this.impulse(a, 1.6);
      const wet = a.createGain();
      wet.gain.value = 0.22;
      this.sfxBus.connect(a.destination);
      this.sfxBus.connect(room);
      this.musicBus.connect(a.destination);
      room.connect(wet).connect(a.destination);
      this.applyLevels();
      sfxEngine.attach(a, this.sfxBus);
      const engine = new MusicEngine(a, this.musicBus);
      this.engine = engine;
      // Music is scheduled a little ahead, in small steps.
      setInterval(() => engine.tick(), 50);
      music.bind(engine);
    } catch {
      this.ctx = null;
    }
  }

  /** Volume and mute changed. */
  applyLevels(): void {
    const a = this.ctx;
    if (!a || !this.sfxBus || !this.musicBus) return;
    const t = a.currentTime;
    this.sfxBus.gain.setTargetAtTime(this.muted ? 0 : this.volume, t, 0.05);
    this.musicBus.gain.setTargetAtTime(this.muted ? 0 : this.music * this.volume * 0.6, t, 0.2);
  }

  /** Switches the music to a track (or an old mood name), crossfading over a couple of seconds. */
  setTheme(theme: Theme): void {
    music.follow(theme);
  }

  /** Nodes and voices of the music now (for checks). */
  musicStats() {
    return this.engine?.stats() ?? null;
  }

  // ───────── effects ─────────
  // Synthesized by the sound effect registry (sfx/): the old methods stay as thin wrappers so their
  // callers keep working (sfx/legacy.ts names the sound each plays).

  swing = () => legacySfx('swing');
  ching = () => legacySfx('ching');
  hit = () => legacySfx('hit');
  hurt = () => legacySfx('hurt');
  block = () => legacySfx('block');
  guardBreak = () => legacySfx('guardBreak');
  interrupt = () => legacySfx('interrupt');
  charge = () => legacySfx('charge');
  bolt = () => legacySfx('bolt');
  nine = () => legacySfx('nine');
  dash = () => legacySfx('dash');
  jump = () => legacySfx('jump');
  heal = () => legacySfx('heal');
  train = () => legacySfx('train');
  coin = () => legacySfx('coin');
  boom = () => legacySfx('boom');
  // Cutscene sounds (data/scenes.ts `sfx` cues).
  gongHit = () => legacySfx('gongHit');
  heartbeat = () => legacySfx('heartbeat');
  waves = () => legacySfx('waves');
  whisper = () => legacySfx('whisper');
  land = () => legacySfx('land');

  /** A short decaying room for the reverb. */
  private impulse(a: AudioContext, secs: number): AudioBuffer {
    const len = Math.floor(a.sampleRate * secs);
    const b = a.createBuffer(2, len, a.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = b.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 3;
    }
    return b;
  }
}
